import type { PostHog } from 'posthog-js'
import { useSyncExternalStore } from 'react'

/**
 * What the visitor has chosen for each non-essential purpose. Today that is analytics (PostHog:
 * usage statistics, errors, feedback); a marketing purpose joins it when a marketing tool does.
 * The banner (vanilla-cookieconsent) is the source of truth; this holds its answer for React.
 */
export type ConsentChoice = 'pending' | 'granted' | 'denied'

let analytics: ConsentChoice = 'pending'
const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** True once the visitor has accepted analytics. Nothing is identified or shown before that. */
export function useAnalyticsConsent(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => analytics === 'granted',
    () => false,
  )
}

type ConsentTarget = Pick<
  PostHog,
  'get_explicit_consent_status' | 'opt_in_capturing' | 'opt_out_capturing' | 'reset'
>

/** Passes the visitor's analytics choice on to PostHog, and to the components that wait for it. */
export function applyAnalyticsConsent(posthog: ConsentTarget, granted: boolean) {
  if (granted) {
    if (posthog.get_explicit_consent_status() !== 'granted') {
      posthog.opt_in_capturing({ captureEventName: false })
    }
  } else {
    // Forget the person first: a reset also clears PostHog's own record of the choice
    if (posthog.get_explicit_consent_status() === 'granted') posthog.reset()
    if (posthog.get_explicit_consent_status() !== 'denied') posthog.opt_out_capturing()
  }
  const next = granted ? 'granted' : 'denied'
  if (next === analytics) return
  analytics = next
  for (const listener of listeners) listener()
}

/** `posthog.reset()` for sign-out. It also clears PostHog's record of the choice, so put it back. */
export function resetPostHog(posthog: ConsentTarget) {
  posthog.reset()
  if (analytics !== 'pending') {
    if (analytics === 'granted') posthog.opt_in_capturing({ captureEventName: false })
    else posthog.opt_out_capturing()
  }
}

/** Opens the preferences, from a link in a footer. */
export async function showCookieSettings() {
  const { showPreferences } = await import('vanilla-cookieconsent')
  showPreferences()
}
